import config from 'walden-fast/config/environment';
import type { FeatureFlag } from 'walden-fast/services/feature-flags';
import type {
  PowerBiPageDisplayName,
  PowerBiReportInternalCode,
} from 'walden-fast/types/fast-backend/power-bi';

export type PowerBiReportRoute =
  | 'inbound-outbound'
  | 'inventory'
  | 'order-details'
  | 'master-data'
  | 'transport'
  | 'order-tracking'
  | 'order-to-cash'
  | 'walden-ml'
  | 'walden-orchestration';

export type PowerBiPageRoute =
  | 'external'
  | 'inbound-outbound'
  | 'good-receipts'
  | 'warehouse-deliveries'
  | 'returns'
  | 'inventory'
  | 'dashboard'
  | 'stock-history'
  | 'current-stock'
  | 'quality-status-changes'
  | 'sales-orders'
  | 'back-orders'
  | 'items-master-data'
  | 'customer-master-data'
  | 'delivery-performance'
  | 'delivery-map'
  | 'details-pod'
  | 'details'
  | 'balance-sheet'
  | 'invoices'
  | 'aging'
  | 'walden-ml'
  | 'credit-notes'
  | 'system-availability'
  | 'security'
  | 'hr'
  | 'resource-commitment'
  | 'revenue-recognition';

export interface PowerBiMapPage {
  displayName: PowerBiPageDisplayName;
  routeName: PowerBiPageRoute;
  externalUrl?: string;
}

export interface PowerBiMap {
  featureFlag: FeatureFlag;
  pages: PowerBiMapPage[];
  reportInternalCode: PowerBiReportInternalCode;
}

export const POWERBI_MAP: Record<PowerBiReportRoute, PowerBiMap> = {
  'inbound-outbound': {
    featureFlag: 'WALDEN_VIEW_INBOUND_OUTBOUND',
    reportInternalCode: 'Warehousing',
    pages: [
      {
        displayName: 'Inbound Outbound',
        routeName: 'inbound-outbound',
      },
      {
        displayName: 'Good receipts',
        routeName: 'good-receipts',
      },
      {
        displayName: 'Warehouse deliveries',
        routeName: 'warehouse-deliveries',
      },
      {
        displayName: 'Returns',
        routeName: 'returns',
      },
      {
        displayName: 'External',
        routeName: 'external',
        externalUrl: config.powerbiExternal.inboundOutbound,
      },
    ],
  },
  inventory: {
    featureFlag: 'WALDEN_VIEW_INVENTORY',
    reportInternalCode: 'Warehousing',
    pages: [
      {
        displayName: 'Inventory',
        routeName: 'inventory',
      },
      {
        displayName: 'Stock history',
        routeName: 'stock-history',
      },
      {
        displayName: 'Current stock',
        routeName: 'current-stock',
      },
      {
        displayName: 'Quality status changes',
        routeName: 'quality-status-changes',
      },
      {
        displayName: 'External',
        routeName: 'external',
        externalUrl: config.powerbiExternal.inventory,
      },
    ],
  },
  'order-details': {
    featureFlag: 'WALDEN_VIEW_ORDER_DETAILS',
    reportInternalCode: 'Warehousing',
    pages: [
      {
        displayName: 'Sales orders',
        routeName: 'sales-orders',
      },
      {
        displayName: 'Back orders',
        routeName: 'back-orders',
      },
      {
        displayName: 'External',
        routeName: 'external',
        externalUrl: config.powerbiExternal.orderDetails,
      },
    ],
  },
  'master-data': {
    featureFlag: 'WALDEN_VIEW_MASTER_DATA',
    reportInternalCode: 'Warehousing',
    pages: [
      {
        displayName: 'Items',
        routeName: 'items-master-data',
      },
      {
        displayName: 'Customers',
        routeName: 'customer-master-data',
      },
      {
        displayName: 'External',
        routeName: 'external',
        externalUrl: config.powerbiExternal.masterData,
      },
    ],
  },
  transport: {
    featureFlag: 'WALDEN_VIEW_TRANSPORT',
    reportInternalCode: 'Transport',
    pages: [
      {
        displayName: 'Delivery performance',
        routeName: 'delivery-performance',
      },
      {
        displayName: 'Delivery map',
        routeName: 'delivery-map',
      },
      {
        displayName: 'Details & POD',
        routeName: 'details-pod',
      },
      {
        displayName: 'External',
        routeName: 'external',
        externalUrl: config.powerbiExternal.transport,
      },
    ],
  },
  'order-tracking': {
    featureFlag: 'WALDEN_VIEW_ORDER_TRACKING',
    reportInternalCode: 'OrderTracking',
    pages: [
      {
        displayName: 'Dashboard',
        routeName: 'dashboard',
      },
      {
        displayName: 'Details',
        routeName: 'details',
      },
      {
        displayName: 'Revenue recognition',
        routeName: 'revenue-recognition',
      },
      {
        displayName: 'External',
        routeName: 'external',
        externalUrl: config.powerbiExternal.orderTracking,
      },
    ],
  },
  'order-to-cash': {
    featureFlag: 'WALDEN_VIEW_ORDER_2_CASH',
    reportInternalCode: 'Order2Cash',
    pages: [
      {
        displayName: 'Dashboard',
        routeName: 'dashboard',
      },
      {
        displayName: 'Balance sheet',
        routeName: 'balance-sheet',
      },
      {
        displayName: 'Invoices',
        routeName: 'invoices',
      },
      {
        displayName: 'Aging',
        routeName: 'aging',
      },
      {
        displayName: 'Credit notes',
        routeName: 'credit-notes',
      },
      {
        displayName: 'External',
        routeName: 'external',
        externalUrl: config.powerbiExternal.order2cash,
      },
    ],
  },
  'walden-ml': {
    featureFlag: 'WALDEN_SERVICE_ML',
    reportInternalCode: 'WaldenML',
    pages: [
      {
        displayName: 'WaldenML',
        routeName: 'dashboard',
      },
      {
        displayName: 'External',
        routeName: 'external',
        externalUrl: config.powerbiExternal.waldenML,
      },
    ],
  },
  'walden-orchestration': {
    featureFlag: 'WALDEN_SERVICE_ORCHESTRATION',
    reportInternalCode: 'Orchestration',
    pages: [
      {
        displayName: 'System Availability',
        routeName: 'system-availability',
      },
      {
        displayName: 'Security',
        routeName: 'security',
      },
      {
        displayName: 'HR',
        routeName: 'hr',
      },
      {
        displayName: 'Resource Commitment',
        routeName: 'resource-commitment',
      },
      {
        displayName: 'External',
        routeName: 'external',
        externalUrl: config.powerbiExternal.waldenOrchestration,
      },
    ],
  },
};

/**
 * List of pages that do not need date range filter
 * Because the data of these pages is always updated and does not depend on the time range
 */
export const DATE_RANGE_FILTER_EXCEPTIONS: PowerBiPageRoute[] = [
  'items-master-data',
  'customer-master-data',
  'current-stock',
  'back-orders',
];

/**
 * Check if a page needs date range filter
 */
export function isDateRangeFilterRequired(
  pageRoute: PowerBiPageRoute,
): boolean {
  return !DATE_RANGE_FILTER_EXCEPTIONS.includes(pageRoute);
}
